import { useState } from 'react';
import { RefreshCw, Search, Film, Presentation, GitBranch } from 'lucide-react';
import type { Campaign, Influencer, Product } from '../../../shared/types';
import { formatWhen } from '../lib/pb';
import { driveThumbnailUrl } from '../lib/drive';
import StageBadge from './StageBadge';

/** Purple tag for admin-only presentation campaigns. */
export function DemoBadge({ compact = false }: { compact?: boolean }) {
  return (
    <span
      className={`zen-pill bg-purple-100 text-purple-700 ${compact ? 'px-1.5 py-0' : ''}`}
      title="Demo campaign — admin-only, uses pre-made assets, nothing is generated or billed"
    >
      <Presentation size={10} />
      Demo
    </span>
  );
}

/** "#12" with a branch icon: this campaign is a revision (a "Fix a scene" clone) of campaign #12. */
export function RevisionBadge({ parentSeq }: { parentSeq?: number }) {
  return (
    <span
      className="zen-pill bg-natural-sage/10 text-natural-sage px-1.5 py-0"
      title={`Revision of campaign #${parentSeq ?? '?'} — created with "Fix a scene" to re-render part of that video`}
    >
      <GitBranch size={10} />
      #{parentSeq ?? '?'}
    </span>
  );
}

/** Tiny round thumb with an initial fallback — influencer face / product bottle. */
function MiniThumb({ url, name }: { url?: string; name?: string }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) {
    return (
      <div className="w-8 h-8 rounded-full bg-natural-sage/15 border-2 border-white flex items-center justify-center text-[10px] font-bold font-serif text-natural-sage shrink-0">
        {(name || '?').charAt(0).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={driveThumbnailUrl(url, 'w100')}
      alt={name ?? ''}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={() => setBroken(true)}
      className="w-8 h-8 rounded-full object-cover border-2 border-white bg-stone-100 shrink-0"
    />
  );
}

interface Props {
  variant: 'sidebar' | 'full';
  campaigns: Campaign[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRefresh: () => void;
}

export default function CampaignList({ variant, campaigns, selectedId, onSelect, onRefresh }: Props) {
  const [query, setQuery] = useState('');

  const filtered = campaigns.filter((c) => {
    if (!query.trim()) return true;
    const influencer = (c.expand?.influencer as Influencer | undefined)?.name ?? '';
    const product = (c.expand?.product as Product | undefined)?.name ?? '';
    const haystack = `${c.seq} ${c.title} ${influencer} ${product} ${c.stage}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  if (variant === 'sidebar') {
    return (
      <div className="space-y-1.5 max-h-[520px] overflow-y-auto -mr-2 pr-2">
        {filtered.length === 0 && (
          <p className="text-xs text-natural-text/40 py-6 text-center leading-relaxed">
            No campaigns yet.
            <br />
            Create one to get started.
          </p>
        )}

        {filtered.map((c) => {
          const influencer = c.expand?.influencer as Influencer | undefined;
          const product = c.expand?.product as Product | undefined;
          const active = c.id === selectedId;

          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              className={`w-full text-left p-3 rounded-2xl border transition-all cursor-pointer ${
                active
                  ? 'border-natural-sage/60 bg-natural-sage/5'
                  : 'border-transparent hover:border-natural-border hover:bg-natural-bg'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <span className="font-mono text-[10px] text-natural-text/40 shrink-0 flex items-center gap-1.5">
                  #{c.seq}
                  {c.is_demo && <DemoBadge compact />}
                  {c.parent_campaign && <RevisionBadge parentSeq={c.parent_seq} />}
                </span>
                <span className="text-[10px] text-natural-text/40 shrink-0">
                  {formatWhen(c.created)}
                </span>
              </div>
              <div className="flex items-center gap-2.5 mb-2">
                {/* Who and what, at a glance — like v1's list. */}
                <div className="flex -space-x-2.5 shrink-0">
                  <MiniThumb
                    url={influencer?.avatar_url || influencer?.base_image_url}
                    name={influencer?.name}
                  />
                  <MiniThumb url={product?.image_url} name={product?.name} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate">
                    {product?.name || c.title || 'Untitled'}
                  </p>
                  <p className="text-[10px] text-natural-text/50 truncate">
                    {influencer?.name ?? '—'}
                  </p>
                </div>
              </div>
              <StageBadge stage={c.stage} />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="zen-card p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-lg font-medium">All campaigns</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-natural-text/35"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="zen-input pl-8 py-2 text-xs w-52"
            />
          </div>
          <button onClick={onRefresh} className="zen-btn-ghost py-2 px-3" title="Refresh">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-xs min-w-[720px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-natural-text/45 border-b border-natural-border">
              <th className="pb-2 font-bold w-14">#</th>
              <th className="pb-2 font-bold">Product</th>
              <th className="pb-2 font-bold">Influencer</th>
              <th className="pb-2 font-bold w-24">Format</th>
              <th className="pb-2 font-bold w-40">Stage</th>
              <th className="pb-2 font-bold w-28">Created</th>
              <th className="pb-2 font-bold w-24 text-right">Video</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const influencer = c.expand?.influencer as Influencer | undefined;
              const product = c.expand?.product as Product | undefined;

              return (
                <tr
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  className="border-b border-natural-border/50 hover:bg-natural-bg cursor-pointer transition-colors"
                >
                  <td className="py-3 font-mono text-[10px] text-natural-text/40">{c.seq}</td>
                  <td className="py-3 font-semibold">
                    <span className="inline-flex items-center gap-2">
                      {product?.name || c.title || 'Untitled'}
                      {c.is_demo && <DemoBadge compact />}
                      {c.parent_campaign && <RevisionBadge parentSeq={c.parent_seq} />}
                    </span>
                  </td>
                  <td className="py-3 text-natural-text/70">{influencer?.name ?? '—'}</td>
                  <td className="py-3 text-natural-text/70 font-mono text-[10px]">
                    {c.aspect_ratio}
                  </td>
                  <td className="py-3">
                    <StageBadge stage={c.stage} />
                  </td>
                  <td className="py-3 text-natural-text/50 text-[10px]">{formatWhen(c.created)}</td>
                  <td className="py-3 text-right">
                    {c.final_video_url || c.final_video_url_uncaptioned ? (
                      <Film size={14} className="inline text-natural-sage" />
                    ) : (
                      <span className="text-natural-text/25">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <p className="text-xs text-natural-text/40 py-10 text-center">
            {query ? 'Nothing matches that search.' : 'No campaigns yet.'}
          </p>
        )}
      </div>
    </div>
  );
}
